<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'
import type { BannerTone } from '../../utils/requestStatusBannerUtils'

// The one-line banner under the header card that answers "what is happening,
// and is it on me?" — an eyebrow (UPDATE / ACTION REQUIRED / COMPLETE), a title,
// one sentence, and the action it asks for in the `actions` slot. Presentational:
// the copy comes from `buildStatusBanner`, the buttons from the container.
defineProps<{
  tone: BannerTone
  eyebrow: string
  title: string
  message: string
  icon: PiIconName
}>()

const EYEBROW_COLOR: Record<BannerTone, string> = {
  info: 'primary',
  warning: 'warning',
  success: 'success',
  danger: 'danger',
  neutral: 'muted',
}
</script>

<template>
  <Box
    is="aside"
    flex
    align="center"
    gap="0.875rem"
    :class="['banner', `banner--${tone}`]"
    role="status"
  >
    <Box flex align="center" justify="center" class="banner__icon-wrap" aria-hidden="true">
      <PiIcon :name="icon" class="banner__icon" />
    </Box>

    <Box direction="col" gap="0.1875rem" grow class="banner__body">
      <Box flex align="center" gap="2" class="banner__heading">
        <Text
          is="span"
          type="micro"
          weight="bold"
          :color="EYEBROW_COLOR[tone]"
          class="banner__eyebrow"
        >
          {{ eyebrow }}
        </Text>
        <Text is="span" type="body" weight="semibold" color="default">{{ title }}</Text>
      </Box>
      <Text type="caption" color="var(--p-surface-700)">{{ message }}</Text>
    </Box>

    <Box v-if="$slots.actions" flex align="center" gap="2" class="banner__actions">
      <slot name="actions" />
    </Box>
  </Box>
</template>

<style scoped>
.banner {
  padding: 0.875rem 1.125rem;
  border: 1px solid;
  border-radius: var(--p-border-radius-lg);
  flex-wrap: wrap;
  row-gap: 0.75rem;
}

.banner__icon-wrap {
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: var(--p-surface-0);
}

.banner__icon {
  font-size: 1rem;
}

.banner__body {
  min-width: 12rem;
}

.banner__heading {
  flex-wrap: wrap;
  row-gap: 0.125rem;
}

.banner__eyebrow {
  letter-spacing: 0.8px;
}

.banner__actions {
  flex-shrink: 0;
  margin-left: auto;
  flex-wrap: wrap;
}

.banner--info {
  background: var(--p-primary-50);
  border-color: var(--p-primary-200);
}

.banner--info .banner__icon-wrap {
  color: var(--p-primary-600);
}

.banner--warning {
  background: var(--p-warning-50);
  border-color: var(--p-warning-200);
}

.banner--warning .banner__icon-wrap {
  color: var(--p-warning-600);
}

.banner--success {
  background: var(--p-success-50);
  border-color: var(--p-success-200);
}

.banner--success .banner__icon-wrap {
  color: var(--p-success-600);
}

.banner--danger {
  background: var(--p-danger-50);
  border-color: var(--p-danger-200);
}

.banner--danger .banner__icon-wrap {
  color: var(--p-danger-600);
}

.banner--neutral {
  background: var(--p-surface-50);
  border-color: var(--p-content-border-color);
}

.banner--neutral .banner__icon-wrap {
  color: var(--p-text-muted-color);
}

@media (max-width: 640px) {
  .banner__actions {
    width: 100%;
    margin-left: 0;
  }
}
</style>
